import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import FeeTable from "../../../components/table/all-tables/feetable/FeeTable";
import DatePickerSelectedDate from "../../../components/datepicker/DatePickerSelectedDate";
import PasswordConfModal from "../../../components/modals/PasswordConfModal";
import {
  InvoiceDetails,
  passwordConfirmModal,
  UserManagment
} from "../../../locales/en";
import {
  CURRENCY_FORMAT,
  showToastType
} from "../../../utils/constants/appConstants";
import { FaTimes } from "react-icons/fa";
import {
  getUpdatOverdueDate,
  getPasswordConfirmation,
  getDeleteOverdueDelete
} from "../../../redux/invoiceDetailsSlice";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  showToast,
  formatCurrencyWithLocale,
  localDateString
} from "../../../utils/utils";
import styles from "./../../../assets/scss/custom.module.scss";
import "./fee.scss";

const Fee = (props) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { invData, fetchInvoiceData } = props;

  const [InvDetails, setInvDetails] = useState({});
  const [enterPassword, setEnterPassword] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [password, setPassword] = useState("");
  const [date, setDate] = useState(null);
  const [tempDate, setTempDate] = useState(null);

  // Memoized fee calculation
  const getFee = useCallback((invData) => {
    if (invData?.internalQuoteStatusId !== 9) {
      return invData?.feeRepayFormattedAmount;
    }

    if (invData?.fundedAt && (invData?.repaymentDate || invData?.dueDate)) {
      const promoDiscount = Math.abs(invData?.promoDiscountAmount || 0);
      const signupDiscount = Math.abs(invData?.signupDiscount || 0);
      const onScheduleAmount = invData?.onScheduleAmount || 0;

      if (invData?.feeUntilDueDate) {
        return (
          invData.feeUntilDueDate +
          promoDiscount +
          signupDiscount +
          onScheduleAmount
        );
      }

      return (
        (invData?.feeTillDueDate || 0) +
        promoDiscount +
        signupDiscount +
        onScheduleAmount
      );
    }

    return null;
  }, []);

  const getRepayFee = useCallback((invData) => {
    if (invData?.fundedAt && (invData?.repaymentDate || invData?.dueDate)) {
      if (invData?.feeUntilDueDate) {
        return invData.feeUntilDueDate + (invData?.onScheduleAmount || 0);
      }

      return (
        (invData?.feeTillDueDate || 0) +
        Math.abs(invData?.signupDiscount || 0) +
        (invData?.onScheduleAmount || 0)
      );
    }

    return null;
  }, []);

  const handleDateChange = (newDate) => {
    setPassword("");
    setTempDate(newDate);
    setEnterPassword(true);
  };

  const handleDeleteOverdueDateBtn = () => {
    setPassword("");
    setShowDeleteModal(true);
  };

  // Memoized password submit handler
  const handlePasswordSubmit = useCallback(async () => {
    try {
      const dataToSend = { password };
      const response = await dispatch(
        getPasswordConfirmation(dataToSend)
      ).unwrap();
      if (response.status === 200 || response.status === 201) {
        setEnterPassword(false);
        setShowDeleteModal(false);
        setPassword("");
        if (tempDate) {
          await handleUpdateOverdueDate(tempDate);
          setDate(tempDate);
          setTempDate(null);
        } else {
          await handleDeleteOverdueDate();
        }
      } else {
        showToast(
          passwordConfirmModal?.validationMessage,
          showToastType?.error
        );
      }
    } catch {
      // handle catch here;
    }
  }, [dispatch, password, tempDate]);

  const handleUpdateOverdueDate = useCallback(
    async (newDate) => {
      try {
        const payload = {
          quoteIds: [id],
          inDefaultDate: localDateString(newDate)
        };
        await dispatch(getUpdatOverdueDate(payload)).unwrap();
      } catch {
        // handle catch here;
      }
    },
    [dispatch, id]
  );

  const handleDeleteOverdueDate = useCallback(async () => {
    try {
      const payload = { quoteIds: [id] };
      const response = await dispatch(getDeleteOverdueDelete(payload)).unwrap();
      if (response.status === 200) fetchInvoiceData();
    } catch {
      // handle catch here;
    }
  }, [dispatch, id, fetchInvoiceData]);

  useEffect(() => {
    if (invData) {
      setInvDetails(invData);
      setDate(invData?.inDefaultDate ? new Date(invData?.inDefaultDate) : null);
    }
  }, [invData]);

  return (
    <>
      <div className={styles.titleArea}>
        <h5>{InvoiceDetails?.feeSection?.title}</h5>
      </div>

      <div className="row">
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle} border-bottom`}>
              <h5>{InvoiceDetails?.feeSection?.quotedFee}</h5>
            </div>
            <p>
              {InvDetails?.feeAmount && (
                <>
                  {CURRENCY_FORMAT}
                  {formatCurrencyWithLocale(InvDetails?.feeAmount)}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle} border-bottom`}>
              <h5>
                {InvoiceDetails?.feeSection?.title} ({InvDetails?.feeRepayText})
              </h5>
            </div>
            <p>
              {getFee(invData) !== null && (
                <>
                  {CURRENCY_FORMAT}
                  {formatCurrencyWithLocale(getFee(invData))}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle} border-bottom`}>
              <h5>{InvoiceDetails?.feeSection?.repayFee}</h5>
            </div>
            <p>
              {getRepayFee(invData) !== null && (
                <>
                  {CURRENCY_FORMAT}
                  {formatCurrencyWithLocale(getRepayFee(invData))}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle} border-bottom`}>
              <h5>{InvoiceDetails?.feeSection?.funded}</h5>
            </div>
            <p>
              {InvDetails?.fundingAmount && (
                <>
                  {CURRENCY_FORMAT}
                  {formatCurrencyWithLocale(InvDetails?.fundingAmount)}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle} border-bottom`}>
              <h5>{InvoiceDetails?.feeSection?.overdueDate}</h5>
            </div>
            {date !== null ? (
              <div className="d-flex dateBtn">
                <DatePickerSelectedDate
                  value={date}
                  onChange={handleDateChange}
                  isReadOnly
                />
                <button
                  className={`${styles.btnPrimary} added-icon`}
                  onClick={handleDeleteOverdueDateBtn}
                >
                  <FaTimes />
                </button>
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-4 mb-3">
          <div className="blog-input-wrap">
            <div className={`${styles.labelTitle} border-bottom`}>
              <h5>{InvoiceDetails?.feeSection?.pricing}</h5>
            </div>
            <p>{InvDetails?.pricingPlan?.pricingPlanName}</p>
          </div>
        </div>
        <div className="col-12 mb-3">
          <div className="blog-input-wrap">
            <FeeTable invData={invData} fetchInvoiceData={fetchInvoiceData} />
          </div>
        </div>
      </div>

      <PasswordConfModal
        title={InvoiceDetails?.feeSection?.passwordModal?.title}
        content={
          UserManagment?.financialsTab?.aiCreditScoreSection?.passwordModal
            ?.subTitle
        }
        isOpen={enterPassword}
        onClose={() => setEnterPassword(false)}
        onSubmit={handlePasswordSubmit}
        password={password}
        setPassword={setPassword}
      />

      <PasswordConfModal
        title={InvoiceDetails?.feeSection?.passwordModal?.deleteTitle}
        content={
          UserManagment?.financialsTab?.aiCreditScoreSection?.passwordModal
            ?.subTitle
        }
        listSub={InvoiceDetails?.feeSection?.passwordModal?.sub}
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onSubmit={handlePasswordSubmit}
        password={password}
        setPassword={setPassword}
      />
    </>
  );
};
Fee.propTypes = {
  invData: PropTypes.object,
  fetchInvoiceData: PropTypes.func
};

export default Fee;
